import { createRef, useEffect, useMemo, useRef, useState } from "react";
import date from "date-and-time";
import { useNavigate } from "react-router-dom";

import FullCalendar from "@fullcalendar/react";
import adaptivePlugin from "@fullcalendar/adaptive";
import {
  CalendarDaysIcon,
  CheckBadgeIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  CircleStackIcon,
  ClockIcon,
  CubeTransparentIcon,
  ExclamationTriangleIcon,
  InboxStackIcon,
  LockClosedIcon,
  LockOpenIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  NoSymbolIcon,
  ShieldCheckIcon,
  TrashIcon,
  TruckIcon,
  UserCircleIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import interactionPlugin, { Draggable } from "@fullcalendar/interaction";

import classNames from "../../utils/classNames";
import { IJob, JobType } from "../../models/Job";
import PriceFormat from "./../../utils/priceFormat";
import { useUserLookup } from "../../hooks/useUserLookup";
import LoadingWheel from "../../components/shared/LoadingWheel";
import CollapsibleSection from "../../components/shared/CollapsibleSection";
import HeadlineStat from "../../components/shared/HeadlineStat";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useJobsByDate from "../../data/useJobsByDate";
import { DateTime } from "luxon";
import formatDateTime from "../../utils/formatDateTime";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import Tabs from "../../components/shared/Tabs/Tabs";
import ContactDocumentsModal from "../../components/contacts/ContactDocumentsModal";
import { setJobLocked, useLockedJobs } from "../../utils/jobLocks";

const numberOrDash = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? "—"
    : value.toLocaleString("en-GB");

const moneyOrDash = (value: number | null | undefined): string =>
  value === null || value === undefined ? "—" : PriceFormat(value);

export default function Jobs() {
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const { saveHandlers } = useDefaultCRUDHandlers("Job");
  // const { jobs, stats, update } = useJobs();
  const [currentDate, setCurrentDate] = useState<string>(DateTime.now().toISODate());
  const { jobs, unscheduledJobs: allUnscheduledJobs, stats, update } = useJobsByDate(currentDate);
  const { isLoading: userLoading, mechanics } = useUserLookup();
  const [isLoading, setIsLoading] = useState(true);
  const [isDraggable, setIsDraggable] = useState(false);
  const [unscheduledJobs, setUnscheduledJobs] = useState<IJob[] | undefined>();
  const [scheduledJobs, setScheduledJobs] = useState<IJob[] | undefined>();
  // The default sort is "Dismantling Priority" — jobs with the highest
  // `partsPriority` set on their donor vehicle (P1 first, P5 last, unset
  // vehicles sink to the bottom). Supervisors can override with the filter
  // dropdown but the page always opens on priority-first ordering.
  const [currentFilter, setCurrentFilter] = useState("Dismantling Priority");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState(0); // 0 = Active, 1 = Archived
  const [statsOpen, setStatsOpen] = useState(true);
  // Dismantling jobs section collapse state — defaults open so the page is
  // immediately useful, but supervisors with a busy backlog can fold the
  // tile grid away to give the planner below more screen real estate.
  const [jobsOpen, setJobsOpen] = useState(true);
  // Right-click assignment / lock menu. Position is viewport-relative; `job`
  // identifies the tile the user right-clicked on. Null = menu closed.
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    job: IJob;
  } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement | null>(null);
  const { isLocked } = useLockedJobs();

  const filters = [
    "Dismantling Priority",
    "VRM A-Z",
    "Number of Parts",
    "Value of Parts High to Low",
    "Value of Parts Low to High",
    "Oldest to Newest",
    "Newest to Oldest",
  ];

  const calendarRef = createRef<FullCalendar>();

  const handleDateChange = (date: Date) => {
    const newDate = DateTime.fromJSDate(date).toISODate()!;

    setCurrentDate(newDate);

    if (calendarRef.current) {
      calendarRef.current.getApi().gotoDate(newDate); // Update view
    }
  }

  // Dismiss the right-click menu when the user clicks outside it, presses
  // Escape, or scrolls the page (any of those indicate intent to move on).
  useEffect(() => {
    if (!contextMenu) return;
    const handleOutside = (e: MouseEvent) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(e.target as Node)
      ) {
        setContextMenu(null);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setContextMenu(null);
    };
    const handleScroll = () => setContextMenu(null);
    window.addEventListener("mousedown", handleOutside);
    window.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [contextMenu]);

  const handleJobContextMenu = (event: React.MouseEvent, job: IJob) => {
    event.preventDefault();
    // Clamp the menu inside the viewport so long mechanic lists don't spill
    // off the bottom/right edge when the tile is right-clicked near a corner.
    const menuWidth = 240;
    const menuHeight = Math.min(320, 48 + (mechanics?.length ?? 0) * 36);
    const x = Math.min(event.clientX, window.innerWidth - menuWidth - 8);
    const y = Math.min(event.clientY, window.innerHeight - menuHeight - 8);
    setContextMenu({ x, y, job });
  };

  const handleAssignMechanic = (job: IJob, mechanicId: string | null) => {
    // Block assignment changes on locked jobs — belt-and-braces with the
    // disabled menu entry so direct calls (e.g. future keyboard shortcut)
    // still respect the lock.
    if (isLocked(job.id)) return;
    const updatedJob: IJob = { ...job, mechanicId };
    // Optimistically refresh the unscheduled list so the assignee chip
    // renders instantly; the server mutation invalidates on success and
    // refetches authoritative state.
    setUnscheduledJobs((prev) =>
      prev?.map((j) => (j.id === job.id ? updatedJob : j))
    );
    update.mutate(updatedJob, {
      onSuccess: () => saveHandlers.onSuccess(),
      onError: () => saveHandlers.onError(),
    });
    setContextMenu(null);
  };

  const handleToggleLock = (jobId: string) => {
    setJobLocked(jobId, !isLocked(jobId));
    setContextMenu(null);
  };

  const handleEventAdd = (
    id: string,
    startDate: Date,
    endDate: Date,
    mechanicId: string
  ) => {
    var existingJob = jobs.data?.data?.find((job) => job.id === id);
    if (existingJob !== undefined) {
      saveHandlers.onError();
      return;
    }

    var foundJob = unscheduledJobs?.find((job) => job.id === id);

    if (endDate === null) {
      //enddate is start date plus duration
      endDate = new Date(startDate);
      endDate.setHours(endDate.getMinutes() + foundJob?.duration!);
    }

    if (foundJob === undefined) {
      saveHandlers.onError();
      return;
    }

    let updatedJob = {
      ...foundJob,
      startDate: date.format(new Date(startDate), "YYYY-MM-DDTHH:mm:ss"),
      endDate: date.format(new Date(endDate), "YYYY-MM-DDTHH:mm:ss"),
      mechanicId: mechanicId,
    };

    //Filter out from the unscheduled jobs
    var updated = unscheduledJobs?.filter((job) => job.id !== updatedJob?.id);
    setUnscheduledJobs(updated);

    update.mutate(updatedJob, {
      onSuccess: () => {
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  const handleEventChange = (
    id: string,
    startDate: Date,
    endDate: Date,
    mechanicId: string
  ) => {
    var foundJob = jobs.data?.data?.find((job) => job.id === id);

    if (foundJob === undefined) {
      saveHandlers.onError();
      return;
    }

    let updatedJob = {
      ...foundJob,
      startDate: date.format(new Date(startDate), "YYYY-MM-DDTHH:mm:ss"),
      endDate: date.format(new Date(endDate), "YYYY-MM-DDTHH:mm:ss"),
      mechanicId: mechanicId,
    };

    //Update the scheduled jobs
    var updated = scheduledJobs?.map((job) => {
      return job.id === updatedJob?.id ? updatedJob : job;
    });
    setScheduledJobs(updated);

    update.mutate(updatedJob, {
      onSuccess: () => {
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  const sortBy = (jobs: IJob[] | undefined) => {
    if (currentFilter === "Dismantling Priority") {
      // Sort by the donor vehicle's `partsPriority` (1 = most urgent, 5 =
      // lowest). `None` (0) and missing values are treated as lowest
      // priority and sink to the bottom so the top of the list is always
      // the actionable stuff. Ties break on `created` (older first) so
      // long-standing priority items don't get leap-frogged by fresh ones.
      return jobs?.slice().sort((a, b) => {
        const rank = (j: IJob) => {
          const p = j.lead?.vehicle?.partsPriority ?? 0;
          return p > 0 ? p : Number.POSITIVE_INFINITY;
        };
        const diff = rank(a) - rank(b);
        if (diff !== 0) return diff;
        return (
          new Date(a.created!).getTime() - new Date(b.created!).getTime()
        );
      });
    } else if (currentFilter === "VRM A-Z") {
      return jobs?.sort((a, b) => {
        return a.title! > b.title! ? 1 : -1;
      });
    } else if (currentFilter === "Number of Parts") {
      return jobs?.sort((a, b) => {
        return b.jobPartCategories?.length! > a.jobPartCategories?.length!
          ? 1
          : -1;
      });
    } else if (currentFilter === "Value of Parts High to Low") {
      // Sort by the job's estimated parts price. `estimatedPrice` is
      // nullable, so fall back to 0 for jobs that haven't been priced yet —
      // those sink to the bottom of the high-to-low list naturally.
      return jobs?.sort((a, b) => {
        return (b.estimatedPrice ?? 0) - (a.estimatedPrice ?? 0);
      });
    } else if (currentFilter === "Value of Parts Low to High") {
      return jobs?.sort((a, b) => {
        return (a.estimatedPrice ?? 0) - (b.estimatedPrice ?? 0);
      });
    } else if (currentFilter === "Oldest to Newest") {
      return jobs?.sort((a, b) => {
        return new Date(a.created!).getTime() >= new Date(b.created!).getTime()
          ? 1
          : -1;
      });
    } else if (currentFilter === "Newest to Oldest") {
      return jobs?.sort((a, b) => {
        return new Date(a.created!).getTime() <= new Date(b.created!).getTime()
          ? 1
          : -1;
      });
    } else {
      return jobs;
    }
  };

  // Keep the planner's event palette in lockstep with the unscheduled tile
  // accents and the legend chips at the top of the card — Body = rose-500,
  // Mechanical = orange-500, Completed = emerald-500 — so the three
  // surfaces read as the same language at a glance.
  const getEventColour = (job: IJob) => {
    if (job.completedOn) {
      return "#10b981"; // emerald-500
    }
    if (job.type === JobType.Body) {
      return "#f43f5e"; // rose-500
    }
    if (job.type === JobType.Mechanical) {
      return "#f97316"; // orange-500
    }
    return "#3b82f6"; // blue-500 fallback
  };

  const isJobArchived = (job: IJob): boolean => {
    // Get all removal dates from parts that have been removed
    const removalDates = job.jobPartCategories
      .map(jpc => jpc.partData?.removedOn)
      .filter((date): date is string => date !== null && date !== undefined)
      .map(date => new Date(date));

    const oneMonthAgo = DateTime.now().minus({ months: 1 }).toJSDate();

    // If no parts have been removed, check if job is older than a month
    if (removalDates.length === 0) {
      const jobCreatedDate = new Date(job.created);
      return jobCreatedDate < oneMonthAgo;
    }

    // Find the most recent removal date
    const mostRecentRemoval = new Date(Math.max(...removalDates.map(d => d.getTime())));

    // Job is archived if the most recent removal was at least a month ago
    return mostRecentRemoval < oneMonthAgo;
  };

  const filterJobsByTab = (jobs: IJob[] | undefined): IJob[] | undefined => {
    if (!jobs) return undefined;

    if (activeTab === 0) {
      // Active tab: show jobs that are not archived
      return jobs.filter(job => !isJobArchived(job));
    } else {
      // Archived tab: show jobs that are archived
      return jobs.filter(job => isJobArchived(job));
    }
  };

  const getJobCounts = (allJobs: IJob[] | undefined): { active: number; archived: number } => {
    if (!allJobs) return { active: 0, archived: 0 };

    const active = allJobs.filter(job => !isJobArchived(job)).length;
    const archived = allJobs.filter(job => isJobArchived(job)).length;

    return { active, archived };
  };

  useEffect(() => {
    setInfo({
      name: "Jobs",
      desc: "View and manage your jobs here",
    });
  }, []);

  useEffect(() => {
    if (!userLoading && !jobs.isLoading && !allUnscheduledJobs.isLoading) {
      let unscheduled = allUnscheduledJobs.data?.data;

      unscheduled = unscheduled?.filter((job) =>
        job.title.toLowerCase().includes(searchTerm.toLowerCase())
      );

      unscheduled = sortBy(unscheduled);

      // Filter by active/archived tab
      unscheduled = filterJobsByTab(unscheduled);

      setUnscheduledJobs(unscheduled);

      let scheduled = jobs.data?.data.filter((job) => job.mechanicId !== null);

      console.log(scheduled)

      setScheduledJobs(scheduled);

      setIsLoading(false);
    }
  }, [
    mechanics,
    currentDate,
    userLoading,
    jobs.data,
    jobs.isLoading,
    allUnscheduledJobs.data,
    allUnscheduledJobs.isLoading,
    currentFilter,
    searchTerm,
    activeTab,
  ]);

  useEffect(() => {
    //If this runs more than once then dragging on will create multiple events
    if (!isDraggable && !isLoading) {
      var containerEl = document.getElementById("job-events");
      if (containerEl) {
        new Draggable(containerEl!, {
          itemSelector: ".fc-event",
          eventData: (eventEl) => {
            return {
              title: eventEl.innerText,
              id: eventEl.id,
              //Need to make sure the minutes are always 2 digits
              duration: `${Math.floor(
                parseInt(eventEl.dataset.duration!) / 60
              )}:${(parseInt(eventEl.dataset.duration!) % 60).toLocaleString(
                "en-GB",
                { minimumIntegerDigits: 2, useGrouping: false }
              )}:00`,
            };
          },
        });
        setIsDraggable(true);
      }
    }
  }, [isLoading]);

  // Memoised per-user dismantling breakdown for the stats table. Falls
  // back to an empty array while the backend hasn't populated the field so
  // the table renders its empty state instead of crashing.
  const dismantlingByUser = useMemo(
    () => stats.data?.data?.dismantlingByUser ?? [],
    [stats.data]
  );

  // Falls back to the sum of the per-user breakdown when the backend
  // hasn't populated `vehiclesDismantledToday` yet — same pattern used on
  // the Awaiting Inventory page for `inventoriedToday`. Kept as `null`
  // when neither source is available so the tile shows an em dash rather
  // than a misleading zero.
  const vehiclesDismantledToday = useMemo(() => {
    const explicit = stats.data?.data?.vehiclesDismantledToday;
    if (explicit !== null && explicit !== undefined) return explicit;
    if (dismantlingByUser.length === 0) return null;
    return dismantlingByUser.reduce(
      (acc, row) => acc + (row.vehiclesDismantled ?? 0),
      0
    );
  }, [stats.data, dismantlingByUser]);

  // Keyed lookup for O(1) access from the FullCalendar resource-label
  // renderer. `dismantlingByUser` rows carry the mechanic's Wilma user id in
  // `userId`, which is what FC uses as the resource id elsewhere in this
  // file (see `resources={mechanics!.map(...)}`), so the join is direct.
  const dismantlingByUserMap = useMemo(() => {
    const map = new Map<string, (typeof dismantlingByUser)[number]>();
    dismantlingByUser.forEach((row) => {
      if (row.userId) map.set(row.userId, row);
    });
    return map;
  }, [dismantlingByUser]);

  // The `dismantlingByUser` feed is scoped to "today" (see
  // docs/pending-endpoints.md → `JobStatsResponse`), so the resource-label
  // summary only makes sense when the planner is pointed at today's view.
  // On any other date we hide the sub-line rather than show stale numbers.
  const isViewingToday = useMemo(
    () => currentDate === DateTime.now().toISODate(),
    [currentDate]
  );

  const dismantlingTotals = useMemo(() => {
    return dismantlingByUser.reduce(
      (acc, row) => ({
        vehiclesDismantled:
          acc.vehiclesDismantled + (row.vehiclesDismantled ?? 0),
        vehiclesDepolluted:
          acc.vehiclesDepolluted + (row.vehiclesDepolluted ?? 0),
        partsDismantled: acc.partsDismantled + (row.partsDismantled ?? 0),
        valueDismantled: acc.valueDismantled + (row.valueDismantled ?? 0),
        partsDamaged: acc.partsDamaged + (row.partsDamaged ?? 0),
        partsDeleted: acc.partsDeleted + (row.partsDeleted ?? 0),
      }),
      {
        vehiclesDismantled: 0,
        vehiclesDepolluted: 0,
        partsDismantled: 0,
        valueDismantled: 0,
        partsDamaged: 0,
        partsDeleted: 0,
      }
    );
  }, [dismantlingByUser]);

  return isLoading ? (
    <LoadingWheel />
  ) : (
    <>
      <div className="mt-2">
        {stats.isLoading ? (
          <LoadingWheel />
        ) : (
          <>
            <button
              type="button"
              onClick={() => setStatsOpen((v) => !v)}
              className="flex w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm hover:bg-slate-50"
              aria-expanded={statsOpen}
            >
              <span>
                {statsOpen
                  ? "Hide dismantling insights"
                  : "Show dismantling insights"}
              </span>
              {statsOpen ? (
                <ChevronUpIcon className="h-4 w-4" />
              ) : (
                <ChevronDownIcon className="h-4 w-4" />
              )}
            </button>

            <CollapsibleSection isOpen={statsOpen}>
              {/* Top-line counts — just the four metrics operators reach for
                  daily: vehicles queued, parts queued, engines queued, and
                  whatever's been pulled off today. The older cost/duration
                  tiles lived here too but were never actioned on this page,
                  so they've been retired in favour of a cleaner header. */}
              <div className="mt-2 grid grid-cols-1 gap-3 px-1 pb-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                <HeadlineStat
                  value={numberOrDash(
                    stats.data?.data?.vehiclesAwaitingDismantling
                  )}
                  label="Vehicles Awaiting Dismantling"
                  tone="cyan"
                  icon={TruckIcon}
                />
                <HeadlineStat
                  value={numberOrDash(vehiclesDismantledToday)}
                  label="Vehicles Dismantled Today"
                  tone="blue"
                  icon={CheckBadgeIcon}
                />
                <HeadlineStat
                  value={numberOrDash(stats.data?.data?.partsAwaitingRemoval)}
                  label="Parts Awaiting Removal"
                  tone="rose"
                  icon={InboxStackIcon}
                />
                <HeadlineStat
                  value={numberOrDash(
                    stats.data?.data?.enginesAwaitingRemoval
                  )}
                  label="Engines Awaiting Removal"
                  tone="violet"
                  icon={CircleStackIcon}
                />
                <HeadlineStat
                  value={numberOrDash(stats.data?.data?.partsRemovedToday)}
                  label="Parts Removed Today"
                  tone="emerald"
                  icon={CubeTransparentIcon}
                />
              </div>

              {/* Per-user dismantling breakdown — mirrors the "Today's
                  Inventory by User" table on the Awaiting Inventory page so
                  floor supervisors can compare the two views at a glance.
                  Columns intentionally match the user's brief: parts
                  dismantled, value dismantled, damaged, deleted. */}
              <div className="mb-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                    Today's Dismantling by User
                  </span>
                  <span className="text-[11px] font-medium text-slate-500">
                    {dismantlingByUser.length}{" "}
                    {dismantlingByUser.length === 1 ? "user" : "users"}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50/60 text-[10px] uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">
                          User
                        </th>
                        <th className="px-3 py-2 text-right font-semibold">
                          <span className="inline-flex items-center gap-1">
                            <TruckIcon
                              className="h-3.5 w-3.5 text-cyan-500"
                              aria-hidden="true"
                            />
                            Vehicles Dismantled
                          </span>
                        </th>
                        <th className="px-3 py-2 text-right font-semibold">
                          <span className="inline-flex items-center gap-1">
                            <ShieldCheckIcon
                              className="h-3.5 w-3.5 text-teal-500"
                              aria-hidden="true"
                            />
                            Vehicles Depolluted
                          </span>
                        </th>
                        <th className="px-3 py-2 text-right font-semibold">
                          <span className="inline-flex items-center gap-1">
                            <WrenchScrewdriverIcon
                              className="h-3.5 w-3.5 text-slate-400"
                              aria-hidden="true"
                            />
                            Parts Dismantled
                          </span>
                        </th>
                        <th className="px-3 py-2 text-right font-semibold">
                          Value Dismantled
                        </th>
                        <th className="px-3 py-2 text-right font-semibold">
                          <span className="inline-flex items-center gap-1">
                            <ExclamationTriangleIcon
                              className="h-3.5 w-3.5 text-amber-500"
                              aria-hidden="true"
                            />
                            Damaged
                          </span>
                        </th>
                        <th className="px-3 py-2 text-right font-semibold">
                          <span className="inline-flex items-center gap-1">
                            <TrashIcon
                              className="h-3.5 w-3.5 text-rose-500"
                              aria-hidden="true"
                            />
                            Deleted
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {dismantlingByUser.length === 0 ? (
                        <tr>
                          <td
                            colSpan={7}
                            className="px-3 py-6 text-center text-xs text-slate-400"
                          >
                            No dismantling activity yet today.
                          </td>
                        </tr>
                      ) : (
                        dismantlingByUser.map((row) => (
                          <tr
                            key={row.userId ?? row.userName}
                            className="hover:bg-slate-50"
                          >
                            <td className="px-3 py-2 text-slate-800">
                              {row.userName}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-cyan-700">
                              {numberOrDash(row.vehiclesDismantled)}
                            </td>
                            <td
                              className={classNames(
                                "px-3 py-2 text-right font-semibold",
                                (row.vehiclesDepolluted ?? 0) > 0
                                  ? "text-teal-700"
                                  : "text-slate-500"
                              )}
                            >
                              {numberOrDash(row.vehiclesDepolluted)}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-slate-800">
                              {numberOrDash(row.partsDismantled)}
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-emerald-700">
                              {moneyOrDash(row.valueDismantled)}
                            </td>
                            <td
                              className={classNames(
                                "px-3 py-2 text-right font-semibold",
                                (row.partsDamaged ?? 0) > 0
                                  ? "text-amber-700"
                                  : "text-slate-500"
                              )}
                            >
                              {numberOrDash(row.partsDamaged)}
                            </td>
                            <td
                              className={classNames(
                                "px-3 py-2 text-right font-semibold",
                                (row.partsDeleted ?? 0) > 0
                                  ? "text-rose-700"
                                  : "text-slate-500"
                              )}
                            >
                              {numberOrDash(row.partsDeleted)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {dismantlingByUser.length > 0 && (
                      <tfoot className="border-t border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wide">
                        <tr>
                          <td className="px-3 py-2 font-semibold text-slate-600">
                            Total
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-cyan-700">
                            {numberOrDash(dismantlingTotals.vehiclesDismantled)}
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-teal-700">
                            {numberOrDash(dismantlingTotals.vehiclesDepolluted)}
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-slate-800">
                            {numberOrDash(dismantlingTotals.partsDismantled)}
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-emerald-700">
                            {moneyOrDash(dismantlingTotals.valueDismantled)}
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-amber-700">
                            {numberOrDash(dismantlingTotals.partsDamaged)}
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-rose-700">
                            {numberOrDash(dismantlingTotals.partsDeleted)}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </CollapsibleSection>
          </>
        )}
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {/* Dismantling jobs section — header row. Sort on the left, title +
            legend in the middle, search on the right. Grid-based so the
            three regions don't shove each other around when the window
            width changes. */}
        <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          {/* Clickable title region — toggles the section open/closed. Sort
              and search controls on the right stay interactive regardless
              (event propagation is stopped there) so a supervisor can
              change sort order without having to expand the section first. */}
          <button
            type="button"
            onClick={() => setJobsOpen((v) => !v)}
            aria-expanded={jobsOpen}
            aria-controls="dismantling-jobs-body"
            className="flex items-center gap-3 rounded-md -m-1 p-1 text-left hover:bg-slate-100/60 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
          >
            {jobsOpen ? (
              <ChevronUpIcon
                className="h-4 w-4 text-slate-500"
                aria-hidden="true"
              />
            ) : (
              <ChevronDownIcon
                className="h-4 w-4 text-slate-500"
                aria-hidden="true"
              />
            )}
            <h2 className="text-sm font-semibold text-slate-900">
              Dismantling Jobs
            </h2>
            {/* Count badge — shows filtered list length while expanded, total
                backlog while collapsed. Gives supervisors a breadcrumb of
                how much they're hiding. */}
            <span className="inline-flex items-center rounded-full bg-slate-200/70 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
              {jobsOpen
                ? (unscheduledJobs?.length ?? 0)
                : (allUnscheduledJobs.data?.data?.length ?? 0)}
            </span>
            <span className="hidden items-center gap-3 text-[11px] text-slate-500 sm:inline-flex">
              <LegendDot tone="rose" label="Body" />
              <LegendDot tone="orange" label="Mechanical" />
              <LegendDot tone="emerald" label="Completed" />
            </span>
          </button>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <div className="relative">
              <select
                aria-label="Sort jobs"
                onChange={(e) => setCurrentFilter(e.target.value)}
                className="appearance-none rounded-md border border-slate-300 bg-white py-1.5 pl-3 pr-8 text-sm text-slate-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {filters.map((filter) => (
                  <option key={filter}>{String(filter)}</option>
                ))}
              </select>
              <ChevronDownIcon
                className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                aria-hidden="true"
              />
            </div>

            <div className="relative">
              <MagnifyingGlassIcon
                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                aria-hidden="true"
              />
              <input
                type="text"
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-md border border-slate-300 py-1.5 pl-8 pr-3 text-sm placeholder-slate-400 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:w-64"
                placeholder="Make, model, VRM, type"
              />
            </div>
          </div>
        </div>

        {/* Everything below the header is the collapsible body. Wrapping
            both the tabs and the tile grid keeps the count badge + sort +
            search always visible even while collapsed, so supervisors can
            tweak filters and watch the count update without re-expanding. */}
        <CollapsibleSection isOpen={jobsOpen}>
          <div id="dismantling-jobs-body">
            {/* Tabs */}
            <div className="px-4 pt-3">
              {(() => {
                // Get all unscheduled jobs (before tab filtering) for counts
                const allUnscheduled =
                  allUnscheduledJobs.data?.data?.filter((job) =>
                    job.title.toLowerCase().includes(searchTerm.toLowerCase())
                  ) || [];
                const counts = getJobCounts(allUnscheduled);

                return (
                  <Tabs
                    viewType={activeTab}
                    viewTypes={[
                      { value: 0, label: `Active (${counts.active})` },
                      { value: 1, label: `Archived (${counts.archived})` },
                    ]}
                    setViewType={setActiveTab}
                  />
                );
              })()}
            </div>

        {/* Unscheduled job tiles — each card is an `.fc-event` so FullCalendar
            can drag it onto the planner below. Border-left accent signals
            job type, pill row gives parts/duration at a glance, and a status
            chip in the corner shows whether the donor's been collected.
            The grid is capped at ~28rem tall with internal scroll so the
            planner below is always in view regardless of backlog depth. */}
        <div id="job-events" className="px-4 pt-4 pb-2">
          {unscheduledJobs?.length ? (
            <div className="max-h-[28rem] overflow-y-auto rounded-lg border border-slate-100 bg-slate-50/40 p-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {unscheduledJobs.map((job) => {
                const isBody = job.type === JobType.Body;
                const accent = isBody
                  ? "before:bg-rose-500"
                  : "before:bg-orange-500";
                const partCount = job.jobPartCategories.length;
                const isDepolluted = !!job.lead?.vehicle?.depollutionId;
                const yardLocationName =
                  job.lead?.vehicle?.yardLocation?.name ?? null;
                // Days-on-site counts from `job.collectedOn` (the date the
                // donor arrived at the yard). If the vehicle hasn't been
                // collected yet we leave the chip off entirely so mechanics
                // don't mistake "0d" for "just arrived".
                const daysOnSite = (() => {
                  if (!job.collectedOn) return null;
                  const dt = DateTime.fromISO(job.collectedOn);
                  if (!dt.isValid) return null;
                  const diff = Math.floor(
                    DateTime.now().diff(dt, "days").days
                  );
                  return diff < 0 ? 0 : diff;
                })();
                const locked = isLocked(job.id);
                const assignedMechanic = job.mechanicId
                  ? mechanics?.find((m) => m.id === job.mechanicId)
                  : undefined;
                // Priority is stored on the donor vehicle (`partsPriority`
                // enum, `None` = 0 .. `Five` = 5). We treat 1 as most
                // urgent and shade the chip from rose (P1) → slate (P5),
                // falling back to a muted "No priority" chip when the
                // vehicle hasn't been ranked yet so supervisors can spot
                // the gap.
                const partsPriority =
                  job.lead?.vehicle?.partsPriority ?? 0;
                const priorityChipClass =
                  partsPriority === 1
                    ? "bg-rose-50 text-rose-700 ring-rose-200"
                    : partsPriority === 2
                      ? "bg-orange-50 text-orange-700 ring-orange-200"
                      : partsPriority === 3
                        ? "bg-amber-50 text-amber-700 ring-amber-200"
                        : partsPriority === 4
                          ? "bg-sky-50 text-sky-700 ring-sky-200"
                          : partsPriority === 5
                            ? "bg-slate-100 text-slate-700 ring-slate-300"
                            : "bg-slate-50 text-slate-400 ring-slate-200";
                const assignedLabel =
                  assignedMechanic?.fullname ??
                  job.mechanic?.fullname ??
                  null;
                return (
                  <div
                    key={job.id}
                    id={job.id}
                    title={
                      locked
                        ? `${job.title} (locked — right-click to unlock)`
                        : job.title
                    }
                    data-duration={job.duration.toString()}
                    data-description={job.description}
                    onClick={() => {
                      // Locked tiles still navigate — locking only blocks
                      // assignment/drag, not the ability to open the job
                      // detail page to inspect it.
                      navigate(`${job.id}`);
                    }}
                    onContextMenu={(e) => handleJobContextMenu(e, job)}
                    className={classNames(
                      // Omit `fc-event` on locked tiles so FullCalendar's
                      // Draggable (`itemSelector: '.fc-event'`) can't pick
                      // them up. The grid/styling stays identical.
                      locked ? "group" : "fc-event group",
                      "relative overflow-hidden rounded-lg border border-slate-200 bg-white p-3 pl-4 shadow-sm transition",
                      "before:absolute before:left-0 before:top-0 before:h-full before:w-1",
                      accent,
                      locked
                        ? "cursor-pointer opacity-80 ring-1 ring-inset ring-slate-300"
                        : "cursor-grab hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-md active:cursor-grabbing"
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {job.title}
                        </p>
                        <p
                          className={classNames(
                            "mt-0.5 text-[11px] font-medium uppercase tracking-wide",
                            isBody ? "text-rose-600" : "text-orange-600"
                          )}
                        >
                          {isBody ? "Body" : "Mechanical"}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <span
                          title={
                            partsPriority > 0
                              ? `Dismantling priority ${partsPriority} (P1 = most urgent, P5 = lowest)`
                              : "No dismantling priority set on the donor vehicle"
                          }
                          className={classNames(
                            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ring-inset",
                            priorityChipClass
                          )}
                        >
                          {partsPriority > 0
                            ? `P${partsPriority}`
                            : "No priority"}
                        </span>
                        {locked && (
                          // Lock chip — signals the tile is frozen and
                          // won't accept mechanic assignments or drag-to-
                          // planner. Right-clicking exposes an "Unlock" entry.
                          <span
                            title="Locked — right-click to unlock"
                            className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-semibold text-white ring-1 ring-inset ring-slate-900"
                          >
                            <LockClosedIcon
                              className="h-3 w-3"
                              aria-hidden="true"
                            />
                            Locked
                          </span>
                        )}
                        {assignedLabel && (
                          <span
                            title={`Assigned to ${assignedLabel}`}
                            className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-200"
                          >
                            <UserCircleIcon
                              className="h-3 w-3"
                              aria-hidden="true"
                            />
                            <span className="max-w-[8rem] truncate">
                              {assignedLabel}
                            </span>
                          </span>
                        )}
                        {daysOnSite !== null && (
                          <span
                            title={`On site since ${formatDateTime(
                              job.collectedOn!,
                              DateTime.DATE_MED
                            )}`}
                            className={classNames(
                              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset",
                              // Heat-map the backlog: fresh arrivals stay
                              // neutral slate, mid-age (7-13d) warms to amber,
                              // and 14+ days flips to rose so stale donors
                              // stand out at a glance.
                              daysOnSite >= 14
                                ? "bg-rose-50 text-rose-700 ring-rose-200"
                                : daysOnSite >= 7
                                  ? "bg-amber-50 text-amber-700 ring-amber-200"
                                  : "bg-slate-100 text-slate-600 ring-slate-200"
                            )}
                          >
                            <CalendarDaysIcon
                              className="h-3 w-3"
                              aria-hidden="true"
                            />
                            {daysOnSite}d on-site
                          </span>
                        )}
                        {isDepolluted && (
                          // Depollution chip — fires when the donor has a
                          // depollution record attached (`vehicle.depollutionId`
                          // non-null). Gives mechanics an at-a-glance cue that
                          // the vehicle is safe to start pulling parts from.
                          <span
                            title="Depolluted"
                            className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-semibold text-teal-700 ring-1 ring-inset ring-teal-200"
                          >
                            <ShieldCheckIcon
                              className="h-3 w-3"
                              aria-hidden="true"
                            />
                            Depolluted
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                        <WrenchScrewdriverIcon
                          className="h-3 w-3 text-slate-500"
                          aria-hidden="true"
                        />
                        {partCount} {partCount === 1 ? "part" : "parts"}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                        <ClockIcon
                          className="h-3 w-3 text-slate-500"
                          aria-hidden="true"
                        />
                        {job.duration} mins
                      </span>
                      {yardLocationName && (
                        <span
                          title={`Yard location: ${yardLocationName}`}
                          className="inline-flex min-w-0 items-center gap-1 rounded-md bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700 ring-1 ring-inset ring-sky-200"
                        >
                          <MapPinIcon
                            className="h-3 w-3 shrink-0 text-sky-600"
                            aria-hidden="true"
                          />
                          <span className="truncate max-w-[10rem]">
                            {yardLocationName}
                          </span>
                        </span>
                      )}
                      {job.estimatedPrice !== null &&
                        job.estimatedPrice !== undefined && (
                          <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                            {PriceFormat(job.estimatedPrice)}
                          </span>
                        )}
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/50 py-10 text-center">
              <InboxStackIcon
                className="h-8 w-8 text-slate-300"
                aria-hidden="true"
              />
              <p className="mt-2 text-sm font-medium text-slate-600">
                No unscheduled jobs
              </p>
              <p className="text-xs text-slate-400">
                Jobs you create will appear here ready to drag onto the
                planner.
              </p>
            </div>
          )}
        </div>
          </div>
        </CollapsibleSection>

        {/* Planner — date nav, then the FullCalendar timeline. The date nav
            uses a compact prev/today/next chevron group instead of three
            equally-weighted buttons so the page reads more like a
            scheduler. */}
        <div className="border-t border-slate-200 px-4 pb-4 pt-5">
          <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-100">
                <CalendarDaysIcon className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Planner
                </p>
                <h2 className="text-lg font-semibold text-slate-900">
                  {formatDateTime(currentDate, DateTime.DATE_HUGE)}
                </h2>
              </div>
            </div>

            <div className="inline-flex items-center rounded-md border border-slate-300 bg-white shadow-sm">
              <button
                type="button"
                onClick={() =>
                  handleDateChange(
                    DateTime.fromISO(currentDate).minus({ days: 1 }).toJSDate()
                  )
                }
                className="flex h-8 items-center justify-center px-2 text-slate-500 hover:bg-slate-50"
                aria-label="Previous day"
              >
                <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => handleDateChange(DateTime.now().toJSDate())}
                className="h-8 border-x border-slate-300 px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() =>
                  handleDateChange(
                    DateTime.fromISO(currentDate).plus({ days: 1 }).toJSDate()
                  )
                }
                className="flex h-8 items-center justify-center px-2 text-slate-500 hover:bg-slate-50"
                aria-label="Next day"
              >
                <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="fc-planner-wilma overflow-hidden rounded-lg border border-slate-200 bg-white">
            <FullCalendar
          ref={calendarRef}
          headerToolbar={false}
          schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
          viewClassNames={"timeline"}
          plugins={[resourceTimeGridPlugin, interactionPlugin, adaptivePlugin]}
          initialView="resourceTimeGridDay"
          slotMinTime={"06:00:00"}
          slotMaxTime={"20:00:00"}
          resources={mechanics!.map((mech) => ({
            id: mech.id,
            title: `${mech.forename} ${mech.surname}`,
          }))}
          resourceOrder="title"
          resourceLabelContent={(arg) => {
            // FC calls this for every resource column header. We render the
            // mechanic's name as the primary label and, when viewing today,
            // append a compact "Np · Mv" summary (parts · vehicles) in a
            // muted row so supervisors get a quick throughput read without
            // leaving the planner. Stats come from `dismantlingByUser`.
            const stat = dismantlingByUserMap.get(arg.resource.id);
            const parts = stat?.partsDismantled ?? 0;
            const vehicles = stat?.vehiclesDismantled ?? 0;
            const hasActivity = parts > 0 || vehicles > 0;
            return (
              <div className="flex flex-col items-center justify-center py-1.5 leading-tight">
                <span className="text-sm font-semibold text-slate-800">
                  {arg.resource.title}
                </span>
                {isViewingToday && (
                  <span
                    title={
                      hasActivity
                        ? `${parts} parts dismantled from ${vehicles} vehicle${vehicles === 1 ? "" : "s"} today`
                        : "No dismantling activity yet today"
                    }
                    className={classNames(
                      "mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
                      hasActivity
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                        : "bg-slate-50 text-slate-400 ring-slate-200"
                    )}
                  >
                    <WrenchScrewdriverIcon
                      className="h-3 w-3"
                      aria-hidden="true"
                    />
                    {parts}
                    <span className="text-slate-300">·</span>
                    <TruckIcon className="h-3 w-3" aria-hidden="true" />
                    {vehicles}
                  </span>
                )}
              </div>
            );
          }}
          titleFormat={{
            month: "long",
            year: "numeric",
            day: "numeric",
            weekday: "long",
          }}
          editable={true}

          defaultTimedEventDuration="01:00:00"
          businessHours={{
            daysOfWeek: [1, 2, 3, 4, 5],
            startTime: "08:00",
            endTime: "18:00",
          }}
          eventDurationEditable={true}
          eventStartEditable={true}
          // resourceAreaWidth="15%"
          droppable={true}
          nowIndicator={true}
          contentHeight="auto"
          allDaySlot={false}
          events={scheduledJobs?.map((job) => ({
            id: job.id,
            title: `${job.title} - ${job.jobPartCategories.length} Part${job.jobPartCategories.length > 1 ? "s" : ""
              }`,
            start: job.startDate ?? new Date(),
            end: job.endDate ?? new Date(),
            resourceId: job.mechanicId ?? "",
            backgroundColor: getEventColour(job),
            borderColor: getEventColour(job),
            duration: job.duration,
            // Stash flags on `extendedProps` so the custom `eventContent`
            // renderer below can decorate events (e.g. a depollution shield)
            // without needing to re-look-up the IJob record from the event id.
            extendedProps: {
              isDepolluted: !!job.lead?.vehicle?.depollutionId,
            },
          }))}
          eventContent={(arg) => {
            // Replace the default "time • title" with the same layout plus a
            // depollution shield when the donor has been depolluted. Keeps
            // the text-only time/title fallback so FC's default typography
            // rules (selected in the CSS above) still apply to them.
            const { isDepolluted } = arg.event.extendedProps as {
              isDepolluted?: boolean;
            };
            return (
              <div className="flex items-center gap-1 overflow-hidden">
                {arg.timeText && (
                  <span className="fc-event-time shrink-0">
                    {arg.timeText}
                  </span>
                )}
                <span className="fc-event-title truncate">
                  {arg.event.title}
                </span>
                {isDepolluted && (
                  // Inline SVG (not heroicons) so the fill follows
                  // `currentColor` and the icon auto-adapts to the event's
                  // foreground colour on both light and dark event tones.
                  <span
                    title="Depolluted"
                    className="ml-auto inline-flex shrink-0 items-center"
                    aria-label="Depolluted"
                  >
                    <ShieldCheckIcon
                      className="h-3.5 w-3.5"
                      aria-hidden="true"
                    />
                  </span>
                )}
              </div>
            );
          }}
          eventClick={(info) => {
            navigate(`${info.event.id}`);
          }}
          eventChange={(info) => {
            handleEventChange(
              info.event.id,
              info.event.start!,
              info.event.end!,
              info.event.getResources()[0].id
            );
          }}
          eventReceive={(info) => {
            handleEventAdd(
              info.event.id,
              info.event.start!,
              info.event.end!,
              info.event.getResources()[0].id
            );
          }}
          eventsSet={(allEvents) => {
            //Duplicate checking
            allEvents.forEach((event) => {
              var duplicates = allEvents.filter((e) => e.id === event.id);
              if (duplicates.length > 1) {
                for (let i = 1; i < duplicates.length; i++) {
                  duplicates[i].remove();
                }
              }
            });
          }}
        />
          </div>
        </div>
      </div>

      {/* Right-click menu for unscheduled-job tiles. Rendered as a fixed-
          position overlay at the viewport level so it escapes the scroll
          container's `overflow-y-auto` clip. */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          role="menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          className="fixed z-50 w-60 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl ring-1 ring-black/5"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-slate-100 bg-slate-50 px-3 py-2">
            <p className="truncate text-xs font-semibold text-slate-700">
              {contextMenu.job.title}
            </p>
            <p className="text-[10px] text-slate-400">
              {isLocked(contextMenu.job.id)
                ? "Locked — assignment disabled"
                : contextMenu.job.mechanicId
                  ? "Assigned"
                  : "Unassigned"}
            </p>
          </div>

          <div className="max-h-60 overflow-y-auto py-1">
            <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              Assign to dismantler
            </p>
            {userLoading && (
              <div className="px-3 py-2 text-xs text-slate-400">
                Loading mechanics…
              </div>
            )}
            {!userLoading && (mechanics?.length ?? 0) === 0 && (
              <div className="px-3 py-2 text-xs text-slate-400">
                No mechanics configured.
              </div>
            )}
            {mechanics?.map((mech) => {
              const selected = contextMenu.job.mechanicId === mech.id;
              const disabled = isLocked(contextMenu.job.id);
              return (
                <button
                  key={mech.id}
                  type="button"
                  role="menuitem"
                  disabled={disabled}
                  onClick={() =>
                    handleAssignMechanic(contextMenu.job, mech.id)
                  }
                  className={classNames(
                    "flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition",
                    selected
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-slate-700",
                    disabled
                      ? "cursor-not-allowed opacity-50"
                      : "hover:bg-slate-50"
                  )}
                >
                  <UserCircleIcon
                    className={classNames(
                      "h-4 w-4 shrink-0",
                      selected ? "text-indigo-600" : "text-slate-400"
                    )}
                    aria-hidden="true"
                  />
                  <span className="truncate">{mech.fullname}</span>
                  {selected && (
                    <CheckBadgeIcon
                      className="ml-auto h-4 w-4 text-indigo-600"
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })}
            {contextMenu.job.mechanicId && (
              <button
                type="button"
                role="menuitem"
                disabled={isLocked(contextMenu.job.id)}
                onClick={() =>
                  handleAssignMechanic(contextMenu.job, null)
                }
                className={classNames(
                  "mt-1 flex w-full items-center gap-2 border-t border-slate-100 px-3 py-1.5 text-left text-xs transition",
                  isLocked(contextMenu.job.id)
                    ? "cursor-not-allowed opacity-50 text-slate-500"
                    : "text-rose-600 hover:bg-rose-50"
                )}
              >
                <NoSymbolIcon className="h-4 w-4" aria-hidden="true" />
                Unassign
              </button>
            )}
          </div>

          <button
            type="button"
            role="menuitem"
            onClick={() => handleToggleLock(contextMenu.job.id)}
            className={classNames(
              "flex w-full items-center gap-2 border-t border-slate-100 px-3 py-2 text-left text-xs font-medium transition",
              isLocked(contextMenu.job.id)
                ? "text-emerald-700 hover:bg-emerald-50"
                : "text-slate-700 hover:bg-slate-50"
            )}
          >
            {isLocked(contextMenu.job.id) ? (
              <>
                <LockOpenIcon className="h-4 w-4" aria-hidden="true" />
                Unlock job
              </>
            ) : (
              <>
                <LockClosedIcon className="h-4 w-4" aria-hidden="true" />
                Lock job
              </>
            )}
          </button>
        </div>
      )}
    </>
  );
}

const LEGEND_DOT_TONES: Record<string, string> = {
  rose: "bg-rose-500",
  orange: "bg-orange-500",
  emerald: "bg-emerald-500",
};

// Small legend chip used in the dismantling jobs header to explain the
// colour coding applied to both the unscheduled tiles and the planner
// events (Body = rose, Mechanical = orange, Completed = emerald).
function LegendDot({ tone, label }: { tone: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={classNames(
          "h-2 w-2 rounded-full",
          LEGEND_DOT_TONES[tone] ?? "bg-slate-400"
        )}
      />
      {label}
    </span>
  );
}
